import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPossibleDuplicateProperties,
  getPropertiesMissingCoordinates,
  getRealEstateSourceQuality,
  getRealEstateSources,
  getStaleProperties,
} from "../../../../services/midpointApi";
import DuplicatePropertiesCard from "./DuplicatePropertiesCard";
import MissingCoordinatesCard from "./MissingCoordinatesCard";
import RealEstateSourceQualityDrawer from "./RealEstateSourceQualityDrawer";
import RealEstateSourceTable from "./RealEstateSourceTable";
import StalePropertiesCard from "./StalePropertiesCard";
import AdminStatePanel from "../layout/AdminStatePanel";

const RealEstateSourcesPage = ({
  onClose,
  onOpenRealEstateSourceControls,
}) => {
  const [summary, setSummary] = useState(null);
  const [selectedSourceCode, setSelectedSourceCode] = useState(null);
  const [sourceDetail, setSourceDetail] = useState(null);
  const [globalMissing, setGlobalMissing] = useState([]);
  const [globalStale, setGlobalStale] = useState([]);
  const [globalDuplicates, setGlobalDuplicates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [detailError, setDetailError] = useState("");
  const [missingError, setMissingError] = useState("");
  const [staleError, setStaleError] = useState("");
  const [duplicateError, setDuplicateError] = useState("");

  const loadPage = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      setMissingError("");
      setStaleError("");
      setDuplicateError("");

      const [sourcesData, missingData, staleData, duplicateData] = await Promise.all([
        getRealEstateSources(),
        getPropertiesMissingCoordinates({ limit: 10 }).catch((error) => {
          setMissingError(error.message);
          return { properties: [] };
        }),
        getStaleProperties({ staleDays: 30, limit: 10 }).catch((error) => {
          setStaleError(error.message);
          return { properties: [] };
        }),
        getPossibleDuplicateProperties({ limit: 5 }).catch((error) => {
          setDuplicateError(error.message);
          return { groups: [] };
        }),
      ]);

      setSummary(sourcesData);
      setGlobalMissing(missingData.properties || []);
      setGlobalStale(staleData.properties || []);
      setGlobalDuplicates(duplicateData.groups || []);

      setSelectedSourceCode((current) => {
        if (current) {
          return current;
        }

        return sourcesData.sources?.[0]?.sourceCode || null;
      });
    } catch (error) {
      setSummary(null);
      setErrorMessage(
        error.message || "Unable to load real estate source quality. Is the API running?"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadSourceDetail = useCallback(async (sourceCode) => {
    if (!sourceCode) {
      setSourceDetail(null);
      return;
    }

    try {
      setDetailLoading(true);
      setDetailError("");
      const detail = await getRealEstateSourceQuality(sourceCode, { limit: 10, staleDays: 30 });
      setSourceDetail(detail);
    } catch (error) {
      setSourceDetail(null);
      setDetailError(error.message || "Unable to load source quality detail.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (selectedSourceCode) {
      loadSourceDetail(selectedSourceCode);
    }
  }, [selectedSourceCode, loadSourceDetail]);

  const selectedSource = summary?.sources?.find(
    (source) => source.sourceCode === selectedSourceCode
  );

  const kpis = useMemo(() => {
    if (!summary) return [];
    return [
      { label: "Property Sources", value: summary.totalSources ?? 0 },
      { label: "Total Properties", value: summary.totalProperties ?? 0 },
      { label: "Mapped", value: summary.mappedProperties ?? 0, tone: "green" },
      { label: "Missing Coords", value: summary.missingCoordinates ?? 0, tone: summary.missingCoordinates > 0 ? "amber" : null },
      { label: "Stale Listings", value: summary.staleProperties ?? 0, tone: summary.staleProperties > 0 ? "amber" : null },
      { label: "Duplicates", value: summary.possibleDuplicates ?? 0, tone: summary.possibleDuplicates > 0 ? "red" : null },
    ];
  }, [summary]);

  return (
    <div className="admin-dashboard admin-broker-feeds-dash">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Broker Feeds</h1>
          <p className="admin-dashboard__subtitle">
            Monitor broker, CSV, and property-feed data quality across all sources
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadPage}
            disabled={isLoading}
          >
            Refresh
          </button>
          {onOpenRealEstateSourceControls && (
            <button
              type="button"
              className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
              onClick={onOpenRealEstateSourceControls}
            >
              Source Controls
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      {isLoading ? (
        <AdminStatePanel variant="loading" message="Loading broker feeds…" />
      ) : null}

      {!isLoading && errorMessage ? (
        <AdminStatePanel variant="error" message={errorMessage} onRetry={loadPage} />
      ) : null}

      {!isLoading && !errorMessage && summary ? (
        <>
          <section className="admin-dashboard__kpis">
            {kpis.map((kpi) => (
              <div className="admin-dash__kpi-card" key={kpi.label}>
                <span className="admin-dash__kpi-label">{kpi.label}</span>
                <span
                  className={`admin-dash__kpi-value${
                    kpi.tone === "green"
                      ? " admin-dash__kpi-value--green"
                      : kpi.tone === "red"
                        ? " admin-dash__kpi-value--red"
                        : kpi.tone === "amber"
                          ? " admin-dash__kpi-value--amber"
                          : ""
                  }`}
                >
                  {kpi.value}
                </span>
              </div>
            ))}
          </section>

          <div className="admin-dashboard__grid">
            <section className="admin-dash__card admin-dash__card--wide admin-broker-feeds-dash__table-section">
              <div className="admin-dash__card-header">
                <h2 className="admin-dash__card-title">Source Quality</h2>
                <span className="admin-dash__card-subtitle">
                  Click a source to inspect quality details
                </span>
              </div>
              <RealEstateSourceTable
                sources={summary.sources}
                selectedSourceCode={selectedSourceCode}
                onSelectSource={setSelectedSourceCode}
              />
            </section>

            {(selectedSourceCode || detailLoading || detailError) && (
              <section className="admin-dash__card admin-dash__card--wide">
                <RealEstateSourceQualityDrawer
                  detail={
                    sourceDetail
                      ? {
                          ...sourceDetail,
                          source: { ...sourceDetail.source, health: selectedSource?.health },
                        }
                      : null
                  }
                  isLoading={detailLoading}
                  errorMessage={detailError}
                  onClose={() => setSelectedSourceCode(null)}
                />
              </section>
            )}

            <section className="admin-dash__card admin-broker-feeds-dash__issues-section">
              <div className="admin-dash__card-header">
                <h2 className="admin-dash__card-title">Data Issues</h2>
                <span className="admin-dash__card-subtitle">
                  Recent samples requiring attention
                </span>
              </div>
              <div className="admin-broker-feeds-dash__issues-grid">
                <MissingCoordinatesCard properties={globalMissing} errorMessage={missingError} />
                <StalePropertiesCard properties={globalStale} errorMessage={staleError} />
                <DuplicatePropertiesCard groups={globalDuplicates} errorMessage={duplicateError} />
              </div>
            </section>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default RealEstateSourcesPage;
